import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

export default function PrivacyPolicyPage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-sans text-slate-800 bg-white">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#1AB790] hover:text-[#17a380] transition-colors mb-4"
          >
            ← Back to Home
          </Link>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Effective Date:</strong> 1/11/2025</p>
            <p><strong>Last Updated:</strong> 13/1/2026</p>
          </div>
        </div>

        <div className="prose prose-lg max-w-none space-y-6 sm:space-y-8">
          <div className="space-y-4 mb-8">
            <p className="text-gray-700 leading-relaxed">
              Welcome to Revisr.ai, operated by Revisr Pty Ltd (ACN 693 866 447) trading as Revisr ("Revisr", "we", "us", or "our"). We respect your privacy and are committed to protecting your personal information in accordance with Australian law.
            </p>
          </div>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">1 — Who We Are</h2>
            <p className="text-gray-700 leading-relaxed">
              Revisr is a brand operated by Revisr Pty Ltd (ACN 693 866 447), an Australian technology company based in Victoria.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our platform helps organisations keep their compliance and business documents current, accurate, and secure.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If you have any questions about this policy or how we handle your data, contact us at:
            </p>
            <p className="text-gray-700 leading-relaxed">
              📧 <a href="mailto:support@revisr.ai" className="text-[#1AB790] hover:underline">support@revisr.ai</a> | ✉️ Revisr, Melbourne VIC Australia
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">2 — Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed">
              We collect only what we need to deliver and improve our services.
            </p>
            <div className="space-y-3">
              <p className="text-gray-700 leading-relaxed">
                <strong>Personal & Business Details</strong><br />
                Your name, email address, job title, organisation, phone number, and billing details when you create an account or subscribe.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Uploaded Documents & Content</strong><br />
                Files you upload (Word, PDF, PowerPoint etc.), metadata required for audits (e.g. headings, clauses, timestamps), and revision logs.<br />
                We never sell or use your content to train AI models.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Technical Data</strong><br />
                IP address, device type, browser, session logs, and usage analytics for security and performance.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Integrations (Optional)</strong><br />
                If you connect third-party tools (e.g. SharePoint, HubSpot, NetSuite), we only access the data needed to make those integrations work.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">3 — How We Use Information</h2>
            <p className="text-gray-700 leading-relaxed">
              We use your information to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Provide, secure, and maintain the Revisr platform.</li>
              <li>Personalise your experience and manage your account.</li>
              <li>Send service updates, reminders, or invoices.</li>
              <li>Improve performance, fix bugs, and develop new features.</li>
              <li>Comply with our legal and regulatory obligations.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              We do not sell, rent, or trade your data with anyone.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">4 — Data Storage & Security</h2>
            <p className="text-gray-700 leading-relaxed">
              Your information is stored primarily in Australia using secure, access-controlled cloud infrastructure.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We apply:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Encryption in transit and at rest (TLS 1.3 / AES-256)</li>
              <li>Role-based access controls and multi-factor authentication</li>
              <li>Routine backups and penetration testing</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              If we ever use overseas service providers, we ensure they comply with privacy standards substantially similar to Australia's (APP 8).
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">5 — Your Rights</h2>
            <p className="text-gray-700 leading-relaxed">
              You have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Access and download the personal information we hold about you.</li>
              <li>Request corrections to inaccurate or outdated information.</li>
              <li>Ask us to delete your data when it's no longer required.</li>
              <li>Opt out of marketing at any time.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              To exercise these rights, email <a href="mailto:support@revisr.ai" className="text-[#1AB790] hover:underline">support@revisr.ai</a>. We'll respond within 30 days, as required by the Privacy Act 1988 (Cth) and the Privacy and Data Protection Act 2014 (Vic).
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">6 — Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We keep your information only as long as necessary to deliver our services or meet legal requirements.
            </p>
            <ul className="list-none space-y-2 text-gray-700 ml-0">
              <li>● <strong>Uploaded files</strong> → retained while your account is active or until you delete them.</li>
              <li>● <strong>Account data</strong> → kept for up to 12 months after closure for billing and audit purposes.</li>
              <li>● <strong>Backups</strong> → automatically purged after 90 days.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">7 — Disclosure to Third Parties</h2>
            <p className="text-gray-700 leading-relaxed">
              We may share limited information with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Trusted service providers (hosting, payments, analytics) under confidentiality agreements.</li>
              <li>Government authorities if required by law or court order.</li>
              <li>Other parties only with your explicit consent.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Revisr will never share or sell your documents or client data.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">8 — Cookies & Analytics</h2>
            <p className="text-gray-700 leading-relaxed">
              We use cookies to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Keep you signed in securely.</li>
              <li>Understand site traffic and improve usability.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              You can adjust or disable cookies in your browser settings.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Analytics data is anonymous and used only to enhance performance.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">9 — Data Breaches</h2>
            <p className="text-gray-700 leading-relaxed">
              If a data breach occurs that could cause serious harm, we will:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4">
              <li>Contain and assess the incident.</li>
              <li>Notify affected individuals and the Office of the Australian Information Commissioner (OAIC) as required under the Notifiable Data Breaches scheme.</li>
              <li>Take corrective steps to prevent recurrence.</li>
            </ol>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">10 — Direct Marketing</h2>
            <p className="text-gray-700 leading-relaxed">
              We may occasionally send product updates or newsletters.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You can unsubscribe at any time using the link in our emails or by contacting <a href="mailto:support@revisr.ai" className="text-[#1AB790] hover:underline">support@revisr.ai</a>.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We comply fully with the Spam Act 2003 (Cth).
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">11 — Updates to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this policy to reflect product or legal changes.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The latest version will always appear at <a href="https://www.revisr.ai/privacy" className="text-[#1AB790] hover:underline">www.revisr.ai/privacy</a> with an updated "Last Updated" date.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If the change is significant, we'll notify registered users directly.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">12 — Contact or Complaints</h2>
            <p className="text-gray-700 leading-relaxed">
              Questions, requests, or complaints:
            </p>
            <p className="text-gray-700 leading-relaxed">
              📧 <a href="mailto:support@revisr.ai" className="text-[#1AB790] hover:underline">support@revisr.ai</a>
            </p>
            <p className="text-gray-700 leading-relaxed">
              ✉️ Revisr Pty Ltd, Melbourne VIC Australia
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              If you're not satisfied with our response, you can contact:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Office of the Australian Information Commissioner (OAIC): <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#1AB790] hover:underline">www.oaic.gov.au</a></li>
              <li>Office of the Victorian Information Commissioner (OVIC): <a href="https://www.ovic.vic.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#1AB790] hover:underline">www.ovic.vic.gov.au</a></li>
            </ul>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

